import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FloorplanData } from '../zoneViewer/zoneMapDataType';
import { buildPickPathFromOrdering } from '../pickPath/buildPickPathFromOrdering';
import { PickPathFeatureCollection } from '../pickPath/buildPickPathFeatureCollection';
import { useCustomNumbering } from './useCustomNumbering';
import { useFlowLabel } from '../utils/flowLabel';

interface UseViewPickPathEditingInput {
  /** Saved order read back from the floorplan (fixtureId -> 1-based sequence). */
  savedFixtureOrderingMap: Map<string, number>;
  floorplanData: FloorplanData[];
  store: string;
  floorplanId?: string;
  /** GFC-only writeback to Floor Plan Service. */
  updateFloorplanPickPath?: (input: { input: unknown }) => Promise<unknown>;
  setToasts: React.Dispatch<React.SetStateAction<unknown[]>>;
  /** Gate: only GFC can edit in view mode. */
  enabled: boolean;
}

/**
 * Infers a starting number and step increment from a saved pickPathId set so
 * that re-editing an existing flow preserves its numbering scheme (e.g. a
 * 100/110/120 path reopens with start=100, step=10).
 *
 * - start = smallest saved value (default 1 when empty)
 * - step  = the gap between consecutive values when uniform; otherwise 1
 */
function inferStartAndStep(values: number[]): { start: number; step: number } {
  if (values.length === 0) return { start: 1, step: 1 };
  const sorted = [...values].sort((a, b) => a - b);
  const start = sorted[0];
  if (sorted.length < 2) return { start, step: 1 };
  const firstGap = sorted[1] - sorted[0];
  const uniform = sorted.every((v, i) => i === 0 || v - sorted[i - 1] === firstGap);
  return { start, step: uniform && firstGap > 0 ? firstGap : 1 };
}

/**
 * View-mode pick-path editing for GFC Drop Zones.
 *
 * View-mode pick paths are a flat, store-wide fixture ordering (no zones), so
 * this hook operates on an ordered list of fixtureIds rather than the
 * zone-scoped pickPaths used by the create flow. Editing is decoupled from
 * publish: Save persists only the pickPathId set via updateFloorplan.
 *
 * Numbering: each fixture's pickPathId is `start + index * step`. Defaults to
 * 1/1 (continuous), but the user can set a custom start and step (e.g. 100/10
 * to leave gaps for later insertions). The order is the source of truth; the
 * numbers are derived, so changing start/step re-labels the whole sequence.
 *
 * Interaction: drag across fixtures to lay down the flow in stroke order; Undo
 * drops the last, Clear empties. Partial coverage is allowed — fixtures left
 * out simply have no pickPathId.
 */
export function useViewPickPathEditing({
  savedFixtureOrderingMap,
  floorplanData,
  store,
  floorplanId,
  updateFloorplanPickPath,
  setToasts,
  enabled,
}: UseViewPickPathEditingInput) {
  const queryClient = useQueryClient();
  const flowLabel = useFlowLabel();
  const [isEditingPickPath, setIsEditingPickPath] = useState(false);
  const [editingOrder, setEditingOrder] = useState<string[]>([]);
  const [isSavingPickPath, setIsSavingPickPath] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const { startNumber, stepIncrement, setStartNumber, setStepIncrement, updateStartNumber, updateStepIncrement } =
    useCustomNumbering(1, 1);
  const isDraggingRef = useRef(false);
  const isEditingRef = useRef(false);
  isEditingRef.current = isEditingPickPath;

  const startEditingPickPath = useCallback(() => {
    if (!enabled) return;
    // Seed from the saved order, sorted ascending by sequence.
    const sortedEntries = Array.from(savedFixtureOrderingMap.entries()).sort((a, b) => a[1] - b[1]);
    setEditingOrder(sortedEntries.map(([fixtureId]) => fixtureId));
    // Reopen with the saved scheme so existing gap-numbered flows are preserved.
    const inferred = inferStartAndStep(sortedEntries.map(([, value]) => value));
    setStartNumber(inferred.start);
    setStepIncrement(inferred.step);
    setIsEditingPickPath(true);
  }, [enabled, savedFixtureOrderingMap, setStartNumber, setStepIncrement]);

  const cancelEditingPickPath = useCallback(() => {
    setIsEditingPickPath(false);
    setEditingOrder([]);
    isDraggingRef.current = false;
    setIsDragging(false);
  }, []);

  // Click: toggle a fixture in/out of the path (removal renumbers the rest).
  const toggleFixtureInPath = useCallback((fixtureId: string) => {
    setEditingOrder((prev) =>
      prev.includes(fixtureId) ? prev.filter((id) => id !== fixtureId) : [...prev, fixtureId],
    );
  }, []);

  // Drag: append a fixture if not already in the path (never removes — drag is
  // additive so sweeping over fixtures builds the sequence in stroke order).
  const appendFixture = useCallback((fixtureId: string) => {
    setEditingOrder((prev) => (prev.includes(fixtureId) ? prev : [...prev, fixtureId]));
  }, []);

  const onPointerDownFixture = useCallback(
    (fixtureId: string) => {
      if (!isEditingRef.current) return;
      isDraggingRef.current = true;
      setIsDragging(true);
      appendFixture(fixtureId);
    },
    [appendFixture],
  );

  const onPointerOverFixture = useCallback(
    (fixtureId: string) => {
      if (!isEditingRef.current || !isDraggingRef.current) return;
      appendFixture(fixtureId);
    },
    [appendFixture],
  );

  const onPointerUp = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);
  }, []);

  const undoLastFixture = useCallback(() => {
    setEditingOrder((prev) => prev.slice(0, -1));
  }, []);

  const clearPath = useCallback(() => {
    setEditingOrder([]);
  }, []);

  // Keyboard shortcuts while editing: Cmd/Ctrl+Z undoes the last fixture,
  // Escape cancels editing. Ignored when focus is in a text/number input so
  // typing in the Start/Step fields isn't hijacked.
  useEffect(() => {
    if (!isEditingPickPath) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;
      if (e.key === 'Escape') {
        cancelEditingPickPath();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        undoLastFixture();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEditingPickPath, cancelEditingPickPath, undoLastFixture]);

  // Derived ordering map for rendering and saving: each fixture's number is
  // `startNumber + index * stepIncrement`. Strictly increasing (step >= 1) so
  // draw order is preserved by the overlay's sort-by-value.
  const editingOrderMap = useMemo(() => {
    const map = new Map<string, number>();
    editingOrder.forEach((fixtureId, idx) => map.set(fixtureId, startNumber + idx * stepIncrement));
    return map;
  }, [editingOrder, startNumber, stepIncrement]);

  const editingPickPathLayers: PickPathFeatureCollection = useMemo(
    () => buildPickPathFromOrdering(editingOrderMap, floorplanData),
    [editingOrderMap, floorplanData],
  );

  // Setters that infer or set the numbering scheme are provided by
  // useCustomNumbering above (clamps start >= 0 integer, step >= 1 integer).

  const savePickPath = useCallback(async () => {
    if (!updateFloorplanPickPath || !floorplanId) {
      setToasts((prev) => [
        ...prev,
        {
          id: `pickpath-no-floorplan-${Date.now()}`,
          children: `Cannot save ${flowLabel.lower}: no floorplan loaded.`,
          type: 'error' as const,
        },
      ]);
      return;
    }
    setIsSavingPickPath(true);
    try {
      const fixtures = editingOrder.map((fixtureId, idx) => ({
        fixtureId,
        pickPathId: String(startNumber + idx * stepIncrement),
      }));
      // Fixtures that were in the saved flow but are no longer in editingOrder
      // (Undo / toggle-off / Clear) must be explicitly cleared, otherwise their
      // stale pickPathId stays on the floorplan and the fixture reappears in the
      // flow after refetch. Empty string is the resolver's "unset this field"
      // signal. This also lets a full Clear persist: the payload carries the
      // previously saved fixtures as clears rather than being empty.
      const editingSet = new Set(editingOrder);
      const cleared = Array.from(savedFixtureOrderingMap.keys())
        .filter((fixtureId) => !editingSet.has(fixtureId))
        .map((fixtureId) => ({ fixtureId, pickPathId: '' }));
      const payloadFixtures = [...fixtures, ...cleared];
      if (payloadFixtures.length === 0) {
        // Nothing saved and nothing drawn — saving would send an empty list,
        // which the resolver rejects. Treat as a no-op and just exit editing.
        setIsEditingPickPath(false);
        setEditingOrder([]);
        return;
      }
      await updateFloorplanPickPath({
        input: { storeId: store, floorplanId, fixtures: payloadFixtures },
      });
      // Invalidate the floorplan cache so the next read reflects the just-saved
      // pickPathId set; without this, re-opening the editor would seed from the
      // stale order.
      void queryClient.invalidateQueries({ queryKey: ['GetFloorplan'] });
      setToasts((prev) => [
        ...prev,
        { id: `pickpath-saved-${Date.now()}`, children: `${flowLabel.sentence} updated.`, type: 'success' as const },
      ]);
      setIsEditingPickPath(false);
      setEditingOrder([]);
    } catch {
      setToasts((prev) => [
        ...prev,
        {
          id: `pickpath-error-${Date.now()}`,
          children: `Failed to update ${flowLabel.lower}.`,
          type: 'error' as const,
        },
      ]);
    } finally {
      setIsSavingPickPath(false);
    }
  }, [
    editingOrder,
    startNumber,
    stepIncrement,
    savedFixtureOrderingMap,
    updateFloorplanPickPath,
    floorplanId,
    store,
    setToasts,
    queryClient,
    flowLabel,
  ]);

  return {
    isEditingPickPath,
    isSavingPickPath,
    isDragging,
    startNumber,
    stepIncrement,
    editingOrderMap,
    editingPickPathLayers,
    sequencedCount: editingOrder.length,
    startEditingPickPath,
    cancelEditingPickPath,
    toggleFixtureInPath,
    onPointerDownFixture,
    onPointerOverFixture,
    onPointerUp,
    undoLastFixture,
    clearPath,
    updateStartNumber,
    updateStepIncrement,
    savePickPath,
  };
}
